import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ensureGptLoaded, initializeGPT, enableAndDisplayAds } from './gpt';

export interface TrackingAdContextValue {
  /** Google Ad Manager network ID, e.g. "1234567" */
  networkId: string;
  /** Ad-unit path segment appended after the network ID, e.g. "lobby" */
  basePath: string;
  /** When false, no ad slots are defined/displayed */
  enabled: boolean;
  /** True once gpt.js has loaded and GPT services have been enabled */
  ready: boolean;
  /** Targeting applied to every tracking ad under this provider */
  defaultTargeting?: Record<string, string>;
}

const TrackingAdContext = createContext<TrackingAdContextValue | null>(null);

export interface TrackingAdProviderProps {
  /** Google Ad Manager network ID, e.g. "1234567" */
  networkId: string;
  /** Ad-unit path segment appended after the network ID (default: "lobby") */
  basePath?: string;
  /** Toggle the whole integration off without removing the provider (default: true) */
  enabled?: boolean;
  /** Targeting key/values applied to every tracking ad under this provider */
  defaultTargeting?: Record<string, string>;
  children: React.ReactNode;
}

/**
 * Provider that loads the Google Publisher Tag library once and exposes the
 * shared GAM configuration to <TrackingAd> wrappers below it.
 *
 * Self-contained: depends only on React and the local GPT helpers, so this
 * directory can be lifted into a standalone package for external React systems.
 */
export const TrackingAdProvider: React.FC<TrackingAdProviderProps> = ({
  networkId,
  basePath = 'lobby',
  enabled = true,
  defaultTargeting,
  children,
}) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // The GPT helpers are idempotent (script injected once, init polls existing
    // state), so it is safe to run this on every mount — including React
    // StrictMode's double-invoke in development.
    let cancelled = false;
    ensureGptLoaded();
    initializeGPT()
      .then(() => {
        if (cancelled) return;
        enableAndDisplayAds();
        setReady(true);
      })
      .catch(error => {
        console.error('[TrackingAd] Failed to initialize GPT:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const value = useMemo<TrackingAdContextValue>(
    () => ({ networkId, basePath, enabled, ready, defaultTargeting }),
    [networkId, basePath, enabled, ready, defaultTargeting]
  );

  return <TrackingAdContext.Provider value={value}>{children}</TrackingAdContext.Provider>;
};

/**
 * Access the tracking-ad configuration. Throws if used outside a
 * <TrackingAdProvider> so misuse fails loudly during development.
 */
export const useTrackingAdContext = (): TrackingAdContextValue => {
  const ctx = useContext(TrackingAdContext);
  if (!ctx) {
    throw new Error('useTrackingAdContext must be used within a <TrackingAdProvider>');
  }
  return ctx;
};
